import type { DeployIntegrity, OpsServiceView, ServiceHealth } from '../screens/ops-board';

/**
 * Maps the live `systemStatus` roll-up (pv-mono#70) into the presentational
 * board rows. Deliberately free of Relay's generated types — the screen maps the
 * query result into the plain {@link LiveServiceStatus} shape below, so this
 * mapping (the interesting logic) stays unit-testable without a Relay harness.
 *
 * The backend owns *which* services are monitored (`SystemStatus:Services`), so
 * the board is data-driven: rows come straight from the response.
 */

/** One service's live roll-up, mirrored from the GraphQL `ServiceStatus` shape.
 *  `deploy` / statuses are the raw wire enum values, kept as `string` so an
 *  unrecognised future value degrades to "unknown" rather than a type error. */
export interface LiveServiceStatus {
    name: string;
    /** Deploy-integrity verdict: HEALTHY | SILENT_FALLBACK | DEGRADED | UNKNOWN. */
    deploy: string;
    /** Liveness/readiness probe result. */
    health: { live: boolean; ready: boolean };
    runningVersion: string | null;
    runningCommit: string | null;
    shippedVersion: string | null;
}

/** Friendly labels for the backend's service keys; unknown keys show the raw key. */
const SERVICE_LABELS: Record<string, string> = {
    vaults: 'Vault API',
    accounts: 'Accounts API',
    ota: 'OTA updates',
    gateway: 'API gateway',
};

export function serviceLabel(key: string): string {
    return SERVICE_LABELS[key] ?? key;
}

/**
 * Tenant-scoped Azure portal deep-links. These are *browse* links (they land the
 * operator on the right resource list in the right tenant) rather than a specific
 * resource blade: the status API doesn't yet expose each service's ARM id (it
 * holds them server-side as `SystemStatus:ContainerAppResourceIds`). Surfacing
 * that id per service is the follow-up that upgrades these to per-resource
 * Metrics/Logs links — see pv-frontend#32.
 */
export function azureContainerAppsUrl(tenantId: string): string {
    return `https://portal.azure.com/#@${tenantId}/blade/HubsExtension/BrowseResource/resourceType/Microsoft.App%2FcontainerApps`;
}

export function azureLogsUrl(tenantId: string): string {
    return `https://portal.azure.com/#@${tenantId}/blade/HubsExtension/BrowseResource/resourceType/Microsoft.OperationalInsights%2Fworkspaces`;
}

/** The probe drives liveness; a DEGRADED deploy verdict means no healthy revision
 *  is serving traffic, so it reads as "down" regardless of what the probe says. */
function mapHealth(deploy: string, health: { live: boolean; ready: boolean }): ServiceHealth {
    if (deploy === 'DEGRADED') return 'down';
    if (!health.live) return 'down';
    if (!health.ready) return 'degraded';
    return 'healthy';
}

/** SILENT_FALLBACK is the actionable "running build ≠ released build" drift.
 *  DEGRADED (an outage, surfaced via health) and UNKNOWN aren't a build drift. */
function mapIntegrity(deploy: string): DeployIntegrity {
    switch (deploy) {
        case 'HEALTHY':
            return 'verified';
        case 'SILENT_FALLBACK':
            return 'drift';
        default:
            return 'unknown';
    }
}

function shortCommit(commit: string | null): string {
    return commit ? commit.slice(0, 7) : '—';
}

/** Human sentence for a silent-fallback row: what's serving vs what was shipped. */
function driftDetail(status: LiveServiceStatus): string {
    const running = status.runningVersion ?? 'unknown';
    const shipped = status.shippedVersion ?? 'unknown';
    return `serving ${running} (${shortCommit(status.runningCommit)}) but ${shipped} was released`;
}

export function toServiceView(status: LiveServiceStatus, tenantId: string): OpsServiceView {
    const integrity = mapIntegrity(status.deploy);
    return {
        name: serviceLabel(status.name),
        health: mapHealth(status.deploy, status.health),
        integrity,
        version: status.runningVersion ?? '—',
        commit: shortCommit(status.runningCommit),
        ...(integrity === 'drift' ? { drift: driftDetail(status) } : {}),
        logsUrl: azureLogsUrl(tenantId),
        metricsUrl: azureContainerAppsUrl(tenantId),
    };
}

export function toServiceViews(
    services: readonly LiveServiceStatus[],
    tenantId: string,
): OpsServiceView[] {
    return services.map((status) => toServiceView(status, tenantId));
}
